import React, { useState, useEffect } from "react";
import api from "../../utils/Api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default function ListadoIncidenciasT() {
  const [state, setState] = useState({
    empleados: [],
    filtroEmpleado: "",
    tipo: "",
    estado: "",
    fechaInicio: "",
    fechaFin: "",
    incidencias: [],
    loading: false,
    error: null,
    paginaActual: 1,
    registrosPorPagina: 10,
  });

  // ================= UTILIDADES =================

  const formatearFecha = (fecha) => {
    const [fechaParte] = fecha.split(" ");
    const [a, m, d] = fechaParte.split("-");
    return `${d}/${m}/${a}`;
  };

  // ================= API =================

  const fetchEmpleados = async () => {
    try {
      const res = await api.get("/usuarios");
      setState((prev) => ({ ...prev, empleados: res.data }));
    } catch {
      setState((prev) => ({ ...prev, error: "Error cargando empleados" }));
    }
  };

  const fetchIncidencias = async (filtros = {}) => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const res = await api.post("/incidencias/listado", { params: filtros });
      setState((prev) => ({
        ...prev,
        incidencias: res.data.data,
        paginaActual: 1,
        error: null,
      }));
    } catch {
      setState((prev) => ({
        ...prev,
        error: "Error cargando incidencias",
      }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchEmpleados();
  }, []);

  // ================= PDF =================

  const generarPDF = async () => {
    const { filtroEmpleado, fechaInicio, fechaFin, tipo, estado, empleados } =
      state;

    try {
      const res = await api.post("/incidencias/listado", {
        params: {
          empleado: filtroEmpleado || undefined,
          fechaInicio,
          fechaFin,
          tipo,
          estado,
        },
      });

      const incidencias = res.data.data;

      const empleado = empleados.find(
        (e) => e.id === Number(filtroEmpleado)
      );

      const doc = new jsPDF();

      doc.setFontSize(18);
      doc.setTextColor(254, 127, 37);
      doc.text("Listado de Incidencias", 14, 20);

      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text(
        `Empleado: ${empleado?.nombre || "Todos"}`,
        14,
        30
      );
      doc.text(
        `Periodo: ${fechaInicio || "-"} / ${fechaFin || "-"}`,
        14,
        35
      );

      autoTable(doc, {
        startY: 45,
        head: [["Empleado", "Fecha", "Tipo", "Descripción", "Estado"]],
        body: incidencias.map((i) => [
          i.usuario?.nombre,
          formatearFecha(i.fecha_hora),
          i.tipo,
          i.descripcion,
          i.estado,
        ]),
        theme: "grid",
        headStyles: { fillColor: [254, 127, 37] },
      });

      doc.save("incidencias.pdf");
    } catch {
      setState((prev) => ({ ...prev, error: "Error generando PDF" }));
    }
  };

  // ================= FORM =================

  const handleSubmit = (e) => {
    e.preventDefault();
    const { filtroEmpleado, fechaInicio, fechaFin, tipo, estado } = state;

    if (fechaInicio && fechaFin && fechaInicio > fechaFin) {
      setState((prev) => ({
        ...prev,
        error: "La fecha inicio no puede ser mayor",
      }));
      return;
    }

    fetchIncidencias({
      empleado: filtroEmpleado || undefined,
      fechaInicio,
      fechaFin,
      tipo,
      estado,
    });
  };

  // ================= PAGINACIÓN =================

  const { paginaActual, registrosPorPagina, incidencias } = state;
  const indiceUltimo = paginaActual * registrosPorPagina;
  const indicePrimero = indiceUltimo - registrosPorPagina;

  const incidenciasPaginadas = incidencias.slice(
    indicePrimero,
    indiceUltimo
  );

  const totalPaginas = Math.ceil(
    incidencias.length / registrosPorPagina
  );

  // ================= RENDER =================

  return (
    <div className="container-fluid">
      <h1 className="fs-3 fw-bold">Listado de incidencias</h1>
      <form onSubmit={handleSubmit} className="bg-light p-3 mb-4 rounded">
        <div className="row g-3">
          <div className="col-md-3">
            <label>Empleado</label>
            <select
              className="form-select"
              value={state.filtroEmpleado}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  filtroEmpleado: e.target.value,
                }))
              }
            >
              <option value="">Todos</option>
              {state.empleados.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label>Tipo</label>
            <select
              className="form-select"
              value={state.tipo}
              onChange={(e) =>
                setState((prev) => ({ ...prev, tipo: e.target.value }))
              }
            >
              <option value="">Todos</option>
              <option value="falta">Falta</option>
              <option value="retraso">Retraso</option>
              <option value="incidencia">Incidencia</option>
            </select>
          </div>

          <div className="col-md-2">
            <label>Estado</label>
            <select
              className="form-select"
              value={state.estado}
              onChange={(e) =>
                setState((prev) => ({ ...prev, estado: e.target.value }))
              }
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="resuelta">Resuelta</option>
              <option value="rechazada">Rechazada</option>
            </select>
          </div>

          <div className="col-md-2">
            <label>Fecha inicio</label>
            <input
              type="date"
              className="form-control"
              value={state.fechaInicio}
              onChange={(e) =>
                setState((prev) => ({ ...prev, fechaInicio: e.target.value }))
              }
            />
          </div>

          <div className="col-md-2">
            <label>Fecha fin</label>
            <input
              type="date"
              className="form-control"
              value={state.fechaFin}
              onChange={(e) =>
                setState((prev) => ({ ...prev, fechaFin: e.target.value }))
              }
            />
          </div>

          <div className="col-12 d-flex justify-content-between">
            <button className="btn btn-primary">Filtrar</button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={generarPDF}
              disabled={!incidencias.length}
            >
              Exportar PDF
            </button>
          </div>
        </div>
      </form>

      {state.error && <p className="text-danger">{state.error}</p>}

      <table className="table table-striped">
        <thead>
          <tr>
            <th>Empleado</th>
            <th>Fecha</th>
            <th>Tipo</th>
            <th>Descripción</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {incidenciasPaginadas.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center">
                No hay incidencias
              </td>
            </tr>
          ) : (
            incidenciasPaginadas.map((i) => (
              <tr key={i.id}>
                <td>{i.usuario?.nombre}</td>
                <td>{formatearFecha(i.fecha_hora)}</td>
                <td>{i.tipo}</td>
                <td>{i.descripcion}</td>
                <td>{i.estado}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {totalPaginas > 1 && (
        <nav>
          <ul className="pagination justify-content-center">
            {[...Array(totalPaginas)].map((_, i) => (
              <li
                key={i}
                className={`page-item ${
                  state.paginaActual === i + 1 ? "active" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      paginaActual: i + 1,
                    }))
                  }
                >
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
